import type {
	PayPalButtonsComponent,
	PayPalNamespace,
} from '@paypal/paypal-js';
import { abandonOrder, completePayPalOrder, submitCheckout } from './api';
import { HOOKS } from './constants';
import { dom, getSelectedPaymentMethod } from './dom';
import { hooks } from './hooks';

type CheckoutOrder = Record<string, unknown> & {
	id?: number;
	transaction_id?: string;
};

declare global {
	interface Window {
		paypal?: PayPalNamespace | null;
	}
}

export interface PayPalCheckoutConfig {
	clientId: string;
	paymentAction: 'capture' | 'authorize';
	sandbox: boolean;
	currency: string;
	isGuest: boolean;
	i18n: {
		genericError: string;
		paypalUnavailable: string;
	};
	buildPayload: () => Record<string, unknown>;
}

let sdkPromise: Promise<PayPalNamespace> | null = null;
let buttons: PayPalButtonsComponent | null = null;
let buttonsRenderPromise: Promise<void> | null = null;

// Visually disables the PayPal button area (dim + block clicks) whenever the
// checkout form isn't valid yet (required guest fields, consent), so a user
// can't click into PayPal's own flow from an obviously-incomplete form. This
// never touches the PayPal SDK's own rendered DOM/listeners inside the
// container — only the container's own style — so it can't orphan them the
// way replacing innerHTML would (see the Contracts checkout-lock fix).
function updateButtonGate() {
	if (!dom.paypalButtons) return;

	const errors = hooks.applyFilters(HOOKS.VALIDATION, {}, dom.form) as Record<
		string,
		unknown
	>;
	const isReady = Object.keys(errors).length === 0;

	dom.paypalButtons.style.opacity = isReady ? '' : '0.5';
	dom.paypalButtons.style.pointerEvents = isReady ? '' : 'none';
}

export function initPayPalCheckout(config: PayPalCheckoutConfig) {
	const form = dom.form;
	if (!form || !dom.paypalButtons) return;

	const syncPaymentUi = () => {
		const isPaypal = getSelectedPaymentMethod() === 'paypal';

		if (dom.submitBtn) {
			dom.submitBtn.style.display = isPaypal ? 'none' : '';
		}
		if (dom.paypalButtons) {
			dom.paypalButtons.style.display = isPaypal ? '' : 'none';
		}
		if (dom.paypalNote) {
			dom.paypalNote.style.display = isPaypal ? '' : 'none';
		}

		if (isPaypal) {
			void renderPayPalButtons(config);
		}

		updateButtonGate();
	};

	document
		.querySelectorAll<HTMLInputElement>('[name="payment_method"]')
		.forEach((input) => {
			input.addEventListener('change', syncPaymentUi);
		});

	form.addEventListener('input', updateButtonGate);
	form.addEventListener('change', updateButtonGate);

	syncPaymentUi();
}

function renderPayPalButtons(config: PayPalCheckoutConfig): Promise<void> {
	if (!buttonsRenderPromise) {
		buttonsRenderPromise = createAndRenderPayPalButtons(config);
	}

	return buttonsRenderPromise;
}

async function createAndRenderPayPalButtons(config: PayPalCheckoutConfig) {
	if (!dom.paypalButtons) return;

	if (!config.clientId) {
		hooks.doAction(HOOKS.ERROR, {
			message: config.i18n.paypalUnavailable || config.i18n.genericError,
		});
		return;
	}

	const paypal = await loadPayPalSdk(config);
	let pendingOrder: CheckoutOrder | null = null;

	const abandonPendingOrder = () => {
		if (config.isGuest || !pendingOrder?.id) return;

		const orderId = Number(pendingOrder.id);
		pendingOrder = null;
		void abandonOrder(orderId);
	};

	buttons =
		paypal.Buttons?.({
			style: {
				layout: 'vertical',
				shape: 'rect',
				label: 'paypal',
			},
			createOrder: async () => {
				hooks.doAction(HOOKS.BEFORE_SUBMIT, dom.form);

				const errors = hooks.applyFilters(
					HOOKS.VALIDATION,
					{},
					dom.form,
				) as Record<string, unknown>;

				if (Object.keys(errors).length) {
					hooks.doAction(HOOKS.VALIDATION_FAILED, errors, dom.form);
					hooks.doAction(HOOKS.FINALLY, dom.form);
					throw new Error(config.i18n.genericError);
				}

				if (pendingOrder?.transaction_id) {
					hooks.doAction(HOOKS.FINALLY, dom.form);
					return String(pendingOrder.transaction_id);
				}

				try {
					const endpoint = config.isGuest
						? 'allcoach/v1/orders/guest-checkout'
						: 'allcoach/v1/orders/checkout';
					const json = (await submitCheckout(endpoint, {
						...config.buildPayload(),
						payment_method: 'paypal',
					})) as any;

					pendingOrder = json.data?.orders?.[0] || json.data;
					const paypalOrderId = String(pendingOrder?.transaction_id || '');

					if (!pendingOrder?.id || !paypalOrderId) {
						throw new Error(config.i18n.genericError);
					}

					return paypalOrderId;
				} catch (error) {
					hooks.doAction(HOOKS.ERROR, error, dom.form);
					throw error;
				} finally {
					hooks.doAction(HOOKS.FINALLY, dom.form);
				}
			},
			onApprove: async (data: { orderID?: string }) => {
				if (!pendingOrder?.id || !data.orderID) {
					hooks.doAction(
						HOOKS.ERROR,
						{ message: config.i18n.genericError },
						dom.form,
					);
					return;
				}

				hooks.doAction(HOOKS.BEFORE_SUBMIT, dom.form);

				const errors = hooks.applyFilters(
					HOOKS.VALIDATION,
					{},
					dom.form,
				) as Record<string, unknown>;

				if (Object.keys(errors).length) {
					hooks.doAction(HOOKS.VALIDATION_FAILED, errors, dom.form);
					// PayPal's card form has already closed by this point, so a
					// field-level error alone can go unnoticed — also surface a
					// scrolled-into-view banner (same as API/network errors).
					hooks.doAction(
						HOOKS.ERROR,
						{
							message: Object.values(errors as Record<string, string>).join(
								', ',
							),
						},
						dom.form,
					);
					hooks.doAction(HOOKS.FINALLY, dom.form);
					return;
				}

				const consentCheckbox =
					dom.form?.querySelector<HTMLInputElement>('[name="consent"]');
				const consent = consentCheckbox ? consentCheckbox.checked : true;

				try {
					const json = (await completePayPalOrder(
						Number(pendingOrder.id),
						data.orderID,
						consent,
					)) as any;
					hooks.doAction(HOOKS.SUCCESS, json.data || pendingOrder, dom.form);
				} catch (error) {
					hooks.doAction(HOOKS.ERROR, error, dom.form);
				} finally {
					hooks.doAction(HOOKS.FINALLY, dom.form);
				}
			},
			onCancel: () => {
				abandonPendingOrder();
				hooks.doAction(HOOKS.FINALLY, dom.form);
			},
			onError: (error: unknown) => {
				abandonPendingOrder();
				hooks.doAction(HOOKS.ERROR, error, dom.form);
				hooks.doAction(HOOKS.FINALLY, dom.form);
			},
		}) ?? null;

	await buttons?.render(dom.paypalButtons);
	updateButtonGate();
}

function loadPayPalSdk(config: PayPalCheckoutConfig): Promise<PayPalNamespace> {
	if (window.paypal) {
		return Promise.resolve(window.paypal);
	}

	if (sdkPromise) {
		return sdkPromise;
	}

	sdkPromise = new Promise((resolve, reject) => {
		const script = document.createElement('script');
		const params = new URLSearchParams({
			'client-id': config.clientId,
			currency: config.currency || 'USD',
			intent: config.paymentAction === 'authorize' ? 'authorize' : 'capture',
			debug: config.sandbox ? 'true' : 'false',
		});

		script.src = `https://www.paypal.com/sdk/js?${params.toString()}`;
		script.async = true;
		script.onload = () => {
			if (window.paypal) {
				resolve(window.paypal);
				return;
			}
			reject(new Error(config.i18n.paypalUnavailable));
		};
		script.onerror = () => reject(new Error(config.i18n.paypalUnavailable));
		document.head.appendChild(script);
	});

	return sdkPromise;
}
